import React, { useMemo, useRef } from 'react';
import { styled } from '@compiled/react';
import { useFragment, graphql } from 'react-relay';
import Badge from '@atlaskit/badge';
import { token } from '@atlaskit/tokens';
import { Text } from '@atlaskit/primitives/compiled';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useBespokeEmptyFieldTextExperiment } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-experiment/index.tsx';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
// eslint-disable-next-line jira/styled/no-styled-import-alias
import { ReadViewContainer as BaseReadViewContainer } from '@atlassian/jira-issue-field-inline-edit/src/styled.tsx';
import type { originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadView$key as OriginalEstimateFragment } from '@atlassian/jira-relay/src/__generated__/originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadView.graphql';
import {
	timeTrackingFormatterWithDefaults,
	transformAGGToJiraTimeFormat,
	transformAGGToJiraTimeUnit,
} from '@atlassian/jira-time-tracking-formatter/src/main.tsx';
import type { originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadViewOld_fragmentRef$key as OriginalEstimateFragmentOld } from '@atlassian/jira-relay/src/__generated__/originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadViewOld_fragmentRef.graphql';
import type { originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadViewNew_fragmentRef$key as OriginalEstimateFragmentNew } from '@atlassian/jira-relay/src/__generated__/originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadViewNew_fragmentRef.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { HoverPopover } from '@atlassian/jira-hover-popover/src/ui/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import type { OriginalEstimateReadViewProps } from './types.tsx';
import { useFormatInputValue } from './utils/formatting.tsx';
import messages from './messages.tsx';

/**
 * The OriginalEstimateReadView will show a read only view of OriginalEstimate field.
 * @param props [OriginalEstimateReadViewProps](./types.tsx)
 */
export const OriginalEstimateReadViewOld = ({
	fragmentRef,
	enableHover = false,
}: Omit<OriginalEstimateReadViewProps, 'fragmentRef'> & {
	fragmentRef: OriginalEstimateFragmentOld | null;
}) => {
	const testId = 'issue-field-original-estimate-read-view';
	const data = useFragment<OriginalEstimateFragmentOld>(
		graphql`
			fragment originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadViewOld_fragmentRef on JiraTimeTrackingField {
				originalEstimate {
					timeInSeconds
				}
				timeTrackingSettings {
					workingHoursPerDay
					workingDaysPerWeek
					defaultFormat
					defaultUnit
				}
			}
		`,
		fragmentRef,
	);

	const intl = useIntl();
	const value = data?.originalEstimate?.timeInSeconds;
	const noValueTextExperiment = intl.formatMessage(messages.noValueTextExperiment);
	const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();

	const readTimeString = useMemo(
		() =>
			timeTrackingFormatterWithDefaults(
				value || 0,
				{
					workingHoursPerDay: data?.timeTrackingSettings?.workingHoursPerDay,
					workingDaysPerWeek: data?.timeTrackingSettings?.workingDaysPerWeek,
					timeFormat: transformAGGToJiraTimeFormat(data?.timeTrackingSettings?.defaultFormat),
					defaultUnit: transformAGGToJiraTimeUnit(data?.timeTrackingSettings?.defaultUnit),
				},
				intl,
			),
		[
			intl,
			data?.timeTrackingSettings?.defaultFormat,
			data?.timeTrackingSettings?.defaultUnit,
			data?.timeTrackingSettings?.workingDaysPerWeek,
			data?.timeTrackingSettings?.workingHoursPerDay,
			value,
		],
	);

	const getRenderView = () => {
		const badge = <Badge testId={`${testId}.badge`}>{readTimeString}</Badge>;

		if (enableHover) {
			return <HoverContainer>{badge}</HoverContainer>;
		}

		return badge;
	};

	if (!value && isExperimentEnabled) {
		return <EmptyFieldText emptyText={noValueTextExperiment} />;
	}

	if (!data) {
		return <EmptyFieldText />;
	}

	return (
		<ReadViewContainer>
			<BadgeWrapper data-testid="issue-field-original-estimate-readview-full.ui.original-estimate.test-id">
				{getRenderView()}
			</BadgeWrapper>
		</ReadViewContainer>
	);
};
/**
 * The OriginalEstimateReadView will show a read only view of OriginalEstimate field.
 * @param props [OriginalEstimateReadViewProps](./types.tsx)
 */
export const OriginalEstimateReadViewNew = ({
	fragmentRef,
	shouldRenderHoverPopover,
}: Omit<OriginalEstimateReadViewProps, 'fragmentRef'> & {
	fragmentRef: OriginalEstimateFragmentNew | null;
}) => {
	const data = useFragment<OriginalEstimateFragmentNew>(
		graphql`
			fragment originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadViewNew_fragmentRef on JiraTimeTrackingField {
				originalEstimate {
					timeInSeconds
				}
				timeTrackingSettings {
					workingHoursPerDay
					workingDaysPerWeek
					defaultFormat
					defaultUnit
				}
			}
		`,
		fragmentRef,
	);
	const { formatMessage } = useIntl();
	const noValueTextExperiment = formatMessage(messages.noValueTextExperiment);
	const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();

	const settingsInput = {
		workingHoursPerDay: data?.timeTrackingSettings?.workingHoursPerDay,
		workingDaysPerWeek: data?.timeTrackingSettings?.workingDaysPerWeek,
		defaultFormat: data?.timeTrackingSettings?.defaultFormat,
		defaultUnit: data?.timeTrackingSettings?.defaultUnit,
	};

	const formatInputValue = useFormatInputValue(settingsInput);
	const timeString = useMemo(
		() => (data ? formatInputValue(data.originalEstimate?.timeInSeconds ?? 0) : null),
		[data, formatInputValue],
	);
	const contentRef = useRef(null);

	if (!data?.originalEstimate?.timeInSeconds && isExperimentEnabled) {
		return <EmptyFieldText emptyText={noValueTextExperiment} />;
	}

	if (timeString === null) {
		return <EmptyFieldText />;
	}

	if (shouldRenderHoverPopover) {
		return (
			<HoverPopover contentRef={contentRef} content={timeString}>
				{({ ref }) => (
					// Empty div to allow ellipsis
					// eslint-disable-next-line jira/react/no-empty-divs
					<div>
						<Badge>
							<Text maxLines={1} ref={mergeRefs(ref, contentRef)}>
								{timeString}
							</Text>
						</Badge>
					</div>
				)}
			</HoverPopover>
		);
	}

	return <Badge>{timeString}</Badge>;
};

export const OriginalEstimateReadView = ({
	fragmentRef,
	...props
}: OriginalEstimateReadViewProps) => {
	const data = useFragment<OriginalEstimateFragment>(
		/* eslint-disable @atlassian/relay/graphql-naming */
		graphql`
			fragment originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadView on JiraTimeTrackingField {
				...originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadViewNew_fragmentRef
				...originalEstimate_issueFieldOriginalEstimateReadviewFull_OriginalEstimateReadViewOld_fragmentRef
			}
		`,
		fragmentRef,
	);

	if (fg('jsc_inline_editing_field_refactor')) {
		return <OriginalEstimateReadViewNew fragmentRef={data ?? null} {...props} />;
	}
	return <OriginalEstimateReadViewOld fragmentRef={data ?? null} {...props} />;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReadViewContainer = styled(BaseReadViewContainer)({
	paddingRight: token('space.025'),
	marginRight: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HoverContainer = styled.div({
	cursor: 'pointer',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:hover > span': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		color: `${token('color.text')} !important`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		backgroundColor: `${token('color.background.neutral.hovered')} !important`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
	lineHeight: `${gridSize * 2.5}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BadgeWrapper = styled.div({
	display: 'inline-block',
	position: 'relative',
	marginTop: token('space.negative.075'),
	marginBottom: token('space.negative.075'),
	marginRight: token('space.negative.025'),
	marginLeft: token('space.025'),
});
